import { styled } from 'goober';

import type { StagePillKey } from './spotlight-stage-pill.types';
import { shouldShowStagePillCountBadge } from './spotlight-stage-pill.utils';

interface SpotlightStagePillProps {
  count?: number;
  countAriaLabel?: string;
  stage: StagePillKey;
  stageLabel: string;
}

export const SpotlightStagePill = ({
  count,
  countAriaLabel,
  stage,
  stageLabel,
}: SpotlightStagePillProps) => {
  const showCountBadge = shouldShowStagePillCountBadge(count);

  return (
    <StagePill
      aria-label={showCountBadge ? countAriaLabel : undefined}
      $stage={stage}
    >
      <StagePillLabel>{stageLabel}</StagePillLabel>
      {showCountBadge && count !== undefined ? (
        <CountBadge $stage={stage}>
          <CountBadgeDigit>{count}</CountBadgeDigit>
        </CountBadge>
      ) : null}
    </StagePill>
  );
};

const StagePill = styled('span')<{ $stage: StagePillKey }>`
  ${({ theme }) => theme.typography.spotlight.stagePill}
  align-items: center;
  border-radius: 999px;
  display: inline-flex;
  gap: 6px;
  min-height: 32px;
  padding: 6px 14px;
  white-space: nowrap;

  ${({ theme, $stage }) => {
    const stageColors = theme.colors.spotlight.stage[$stage];
    return `
      background-color: ${stageColors.bg};
      color: ${stageColors.text};
    `;
  }}
`;

const StagePillLabel = styled('span')`
  line-height: 1;
`;

const CountBadge = styled('span')<{ $stage: StagePillKey }>`
  border-radius: 5px;
  display: inline-grid;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  height: 20px;
  line-height: normal;
  min-width: 20px;
  padding: 0 4px;
  place-items: center;

  ${({ theme, $stage }) => {
    const stageColors = theme.colors.spotlight.stage[$stage];
    const countBackground =
      'countBg' in stageColors ? stageColors.countBg : stageColors.bg;

    return `
      background-color: ${countBackground};
      color: ${stageColors.text};
    `;
  }}
`;

const CountBadgeDigit = styled('span')`
  display: block;
  line-height: 1;
  margin-block: calc(0.5cap - 0.5lh);

  @supports (text-box-trim: trim-both) {
    margin-block: 0;
    text-box: trim-both cap alphabetic;
  }
`;
